import { noop } from '../helper'
import { clearTimeout, setTimeout } from '../shortcut'

import type { TFunction, ToAsyncFunction, TPromise } from '../types'

export interface Debounced<T extends TFunction> {
  (...args: Parameters<T>): void
  cancel(): void
  flush(): void
}

export interface DebounceOptions {
  leading?: boolean
  maxWait?: number
  trailing?: boolean
}

export interface PromiseDebounceThrottleOptions {
  debounceTime?: number
  leading?: boolean
  throttleTime?: number
  trailing?: boolean
}

export function debounce<T extends TFunction>(
  fn = noop as T,
  wait = 0,
  { leading = false, maxWait = Infinity, trailing = true }: DebounceOptions = {}
): Debounced<T> {
  let timer: number
  let lastThis: any
  let lastArgs: any[]
  let lastInvoke = 0

  const invoke = (realInvoke: boolean) => {
    clearTimeout(timer)
    if (realInvoke) {
      fn.apply(lastThis, lastArgs)
      lastInvoke = Date.now()
    }
  }

  const trailingInvoke = () => invoke(trailing)
  const leadingInvoke = () => {
    invoke(leading)
    timer = setTimeout(trailingInvoke, wait)
  }

  function debounced(this: any, ...args: any[]) {
    lastThis = this
    lastArgs = args
    clearTimeout(timer)
    const timeSinceLastInvoke = Date.now() - lastInvoke
    if (leading && (lastInvoke === 0 || timeSinceLastInvoke >= wait)) {
      timer = setTimeout(leadingInvoke, 0)
    } else {
      const remainingWait = Math.max(
        0,
        Math.min(wait, maxWait - timeSinceLastInvoke)
      )
      timer = setTimeout(trailingInvoke, remainingWait)
    }
  }

  debounced.flush = () => invoke(true)
  debounced.cancel = () => {
    clearTimeout(timer)
    lastInvoke = 0
  }

  return debounced
}

export function pDebounce<T extends TFunction>(
  fn = noop as T,
  wait = 0,
  options?: DebounceOptions
): ToAsyncFunction<T> {
  let promise: TPromise | undefined
  let promiseResolve: TFunction | undefined

  const debounced = debounce(
    (thisArg, args) => {
      promiseResolve?.(fn.apply(thisArg, args))
      promise = undefined
    },
    wait,
    options
  )

  return function (this: any, ...args: any[]) {
    promise ??= new Promise((resolve) => {
      promiseResolve = resolve
    })
    debounced(this, args)
    return promise
  }
}

export function pDebounceThrottle<T extends TFunction>(
  fn = noop as T,
  options: PromiseDebounceThrottleOptions = {}
): ToAsyncFunction<T> {
  const { debounceTime, throttleTime } = options
  if (typeof debounceTime === 'number')
    return pDebounce(fn, debounceTime, options)
  if (typeof throttleTime === 'number')
    return pThrottle(fn, throttleTime, options)
  return pDebounce(fn, 0, options)
}

export function pThrottle<T extends TFunction>(
  fn?: T,
  wait = 0,
  options?: DebounceOptions
): ToAsyncFunction<T> {
  return pDebounce(fn, wait, { maxWait: wait, ...options })
}

export function throttle<T extends TFunction>(
  fn?: T,
  wait = 0,
  options?: DebounceOptions
): Debounced<T> {
  return debounce(fn, wait, { maxWait: wait, ...options })
}
